import React, { useState, useEffect, useCallback } from 'react';
import { FileSpreadsheet, ArrowRight } from 'lucide-react';
import { iconFor } from '../../utils/navIcons';
import { useDbNavigate } from '../../context/DatabaseContext';
import { useNavigation } from '../../context/NavigationContext';
import { reportingApi } from '../../services/api';

/**
 * ReportsPage — landing/dashboard for the Reports module.
 *
 * The sections and the reports in them are the Reports branch of the person's navigation tree
 * (each section a header row, each report a row under it), so the landing shows exactly what the
 * sidebar does, in the same order and with the same icons. /api/report/configs supplies each
 * report's description. A section with nothing the person may open is not in the tree, so it does
 * not show here either.
 *
 * Adding a report = a row in AppCfg_ModuleReport plus its row in AppCfg_Navigation under a
 * section. No code deploy required.
 */

// Hand-built report pages have no AppCfg_ModuleReport row to carry a description.
const PAGE_DESCRIPTIONS = {
  'reports/oat-commissioning-keys': 'OAT (Owner Acceptance Testing) commissioning key sheets — printable PDF checklists for each tag, with prerequisites, general requirements, and close-out steps.',
};

const ReportsPage = () => {
  const navigate = useDbNavigate();
  const { byKey } = useNavigation();
  const [descriptions, setDescriptions] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportingApi.listConfigs();
      const list = Array.isArray(res?.data) ? res.data : [];
      setDescriptions(Object.fromEntries(list.map(r => [`reports/${r.slug}`, r.description])));
    } catch {
      setDescriptions({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = (byKey('reports')?.children || [])
    .map(section => ({
      id: section.id,
      label: section.label,
      icon: iconFor(section.icon) || FileSpreadsheet,
      reports: (section.children || []).filter(r => r.destination).map(r => ({
        destination: r.destination,
        name: r.label,
        description: descriptions[r.destination] || PAGE_DESCRIPTIONS[r.destination],
      })),
    }))
    .filter(g => g.reports.length > 0);

  const totalReports = groups.reduce((sum, g) => sum + g.reports.length, 0);

  if (loading) {
    return (
      <div className="stui-dashboard-body">
        <div className="stui-dashboard-header">
          <FileSpreadsheet size={20} className="stui-dashboard-header-icon" />
          <h1 className="stui-dashboard-header-title">Reports</h1>
        </div>
        <div className="stui-module-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="stui-dashboard-body">
      <div className="stui-dashboard-header">
        <FileSpreadsheet size={20} className="stui-dashboard-header-icon" />
        <h1 className="stui-dashboard-header-title">Reports</h1>
        <span className="stui-dashboard-header-subtitle">
          {totalReports} {totalReports === 1 ? 'report' : 'reports'} available
        </span>
      </div>

      <div className="stui-dashboard-groups">
        {groups.map(group => {
          const GroupIcon = group.icon;
          return (
            <div key={group.id}>
              <div className="stui-dashboard-group-header">
                <GroupIcon size={16} className="stui-dashboard-header-icon" />
                <span className="stui-dashboard-group-title">{group.label}</span>
                <span className="stui-dashboard-group-count">
                  {group.reports.length} {group.reports.length === 1 ? 'report' : 'reports'}
                </span>
              </div>
              <div className="stui-dashboard-tile-grid">
                {group.reports.map(report => (
                  <div
                    key={report.destination}
                    className="stui-tile stui-clickable"
                    onClick={() => navigate(`/${report.destination}`)}
                  >
                    <div className="stui-tile-header">
                      <span className="stui-tile-title">{report.name}</span>
                      <ArrowRight size={16} className="stui-dashboard-tile-action" />
                    </div>
                    {report.description && (
                      <div className="stui-dashboard-tile-content">
                        <p className="stui-dashboard-tile-description">{report.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsPage;
